import { ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, Output, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { orderBy as _orderBy, partition as _partition } from 'lodash-es';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  NoteDetails, NoteTarget, PinNoteGQL, RemoveNoteGQL, UnpinNoteGQL
} from 'generated/graphql';

import { NotePermissionsService } from 'app/note/note-permissions.service';
import { ToastrService } from 'core/toastr.service';
import { NOTE_CHANGES } from 'app/note/note.actions';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-view-note',
  templateUrl: './view-note.component.html',
  styleUrls: ['./view-note.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class ViewNoteComponent {
  @Input() note: NoteDetails.Fragment;
  @Input() noteCampaignId: string;
  @Input() targetId: string;
  @Input() targetType: NoteTarget;
  @Input() preventEdit: boolean;
  @Input() viewAllMenuItemVisible: boolean;

  /** Is truncation enabled for this note? */
  @Input() truncateEnabled: boolean;

  @Output() viewAllNotesClicked = new EventEmitter<NoteDetails.Fragment>();

  // Add a host binding that will apply 'pinned' class to the note if note.pinnedAt is not null or empty
  @HostBinding('class.pinned')
  get pinned() {
    return this.note?.pinnedAt ? true : false;
  }

  noteMarkedForEdit = null;

  get campaignId() {
    return this.route.snapshot.queryParamMap.get('campaignId');
  }

  constructor(
    private _cd: ChangeDetectorRef,
    private _store: Store,
    private notePermissionService: NotePermissionsService,
    private toastrService: ToastrService,
    private pinNoteGQL: PinNoteGQL,
    private unpinNoteGQL: UnpinNoteGQL,
    private removeNoteGQL: RemoveNoteGQL,
    private route: ActivatedRoute
  ) {
  }

  menuClick(event: MouseEvent) {
    event.stopPropagation();
  }

  isFile(note) {
    return note.isFile === true;
  }

  isPinned(note: NoteDetails.Fragment) {
    return note?.pinnedAt ? true : false;
  }

  togglePinned(note: NoteDetails.Fragment) {
    if (this.isPinned(note)) {
      this.unpinNoteGQL.mutate({
        input: { campaignId: this.campaignId, noteId: note.id }
      })
        .subscribe((res) => {
          this._store.dispatch(NOTE_CHANGES({
            payload: {
              targetId: this.targetId,
              targetType: this.targetType,
              campaignId: this.campaignId
            }
          }));
          this.toastrService.success('Note has been unpinned');
          this._cd.markForCheck();
        });
    } else {
      this.pinNoteGQL.mutate({
        input: { campaignId: this.campaignId, noteId: note.id }
      })
        .subscribe((res) => {
          this._store.dispatch(NOTE_CHANGES({
            payload: {
              targetId: this.targetId,
              targetType: this.targetType,
              campaignId: this.campaignId
            }
          }));
          this._cd.markForCheck();
          this.toastrService.success('Note Pinned');
        });
    }
  }

  shouldShowNoteMenu(note: NoteDetails.Fragment): Observable<boolean> {
    return combineLatest([
      this.canEdit(note),
      this.canRemove(note),
      this.canPinNote(note)
    ]).pipe(
      map(([canEdit, canRemove, canPin]) => canEdit || canRemove || canPin || this.viewAllMenuItemVisible)
    );
  }

  canRemove(note: NoteDetails.Fragment): Observable<boolean> {
    if (this.preventEdit) {
      return of(false);
    }
    return this.notePermissionService.canRemoveNote(note, this.noteCampaignId, this.campaignId);
  }

  canEdit(note: NoteDetails.Fragment): Observable<boolean> {
    if (this.preventEdit) {
      return of(false);
    }
    if (this.targetType === NoteTarget.TaggableLogbook) {
      return of(true);
    }
    return this.notePermissionService.canEditNote(note, this.noteCampaignId, this.campaignId);
  }

  canPinNote(note: NoteDetails.Fragment): Observable<boolean> {
    return this.notePermissionService.canPinNote(note, this.targetType);
  }

  removeNote(note: { id: string }) {
    this.removeNoteGQL.mutate({
      input: { noteIds: [note.id] }
    }, {
      update: (cache, { data: { removeNotes: { success } } }) => {
        if (!success) {
          return;
        }
        cache.evict({
          id: `Note:${note.id}`
        });
      }
    })
      .subscribe({
        next: ({ data: { removeNotes: { success } } }) => {
          if (!success) {
            this.toastrService.error('Note could not be removed');
          } else {
            this.toastrService.success('Note Removed');
          }

          this._store.dispatch(NOTE_CHANGES({
            payload: {
              targetId: this.targetId,
              targetType: this.targetType,
              campaignId: this.campaignId
            }
          }));
        },
        error: (err) => this.toastrService.error(err)
      });
  }

  onViewAllNotesClicked(note: NoteDetails.Fragment) {
    this.viewAllNotesClicked.emit(note);
  }
}
